export type TemplateWebMCPErrorCode =
  | 'EDITOR_NOT_READY'
  | 'INVALID_INPUT'
  | 'UNAUTHORIZED'
  | 'UNEXPECTED'
  | 'STALE_REVISION'
  | 'INVALID_TOKEN'
  | 'UNSUPPORTED'
  | 'PREVIEW_FAILED';

export type TemplateWebMCPSuccess<T> = {
  ok: true;
  data: T;
};

export type TemplateWebMCPFailure = {
  ok: false;
  error: {
    code: TemplateWebMCPErrorCode;
    message: string;
    details?: unknown;
  };
};

export type TemplateWebMCPPayload<T> = TemplateWebMCPSuccess<T> | TemplateWebMCPFailure;

export type TemplateWebMCPContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; data: string; mimeType: 'image/png' };

export type TemplateWebMCPToolResult<T> = {
  content: Array<TemplateWebMCPContentBlock>;
  structuredContent: TemplateWebMCPPayload<T>;
  isError?: true;
};

export function toolSuccess<T>(data: T): TemplateWebMCPSuccess<T> {
  return { ok: true, data };
}

export function toolError(
  code: TemplateWebMCPErrorCode,
  message: string,
  details?: unknown,
): TemplateWebMCPFailure {
  return {
    ok: false,
    error: details === undefined ? { code, message } : { code, message, details },
  };
}

function humanReadable<T>(payload: TemplateWebMCPPayload<T>): string {
  if (!payload.ok) {
    return payload.error.message;
  }

  const data = payload.data as {
    templateId?: unknown;
    readType?: unknown;
    operation?: unknown;
  };
  if (typeof data?.operation === 'string' && data.operation.length > 0) {
    return `Applied ${data.operation} successfully.`;
  }
  if (typeof data?.readType === 'string' && data.readType.length > 0) {
    return `Read ${data.readType} successfully.`;
  }
  if (typeof data?.templateId === 'string' && data.templateId.length > 0) {
    return `Read template ${data.templateId} successfully.`;
  }

  return 'Tool completed successfully.';
}

export function mcpToolResult<T>(
  payload: TemplateWebMCPPayload<T>,
  imagePngBase64?: string,
): TemplateWebMCPToolResult<T> {
  // Harnesses that forward only `content` to the model never see `structuredContent`,
  // so the payload is serialized here as well. See MCP tools spec, structured content.
  const content: TemplateWebMCPContentBlock[] = [
    { type: 'text', text: humanReadable(payload) },
    { type: 'text', text: JSON.stringify(payload) },
  ];
  if (payload.ok && imagePngBase64) {
    content.push({ type: 'image', data: imagePngBase64, mimeType: 'image/png' });
  }
  if (payload.ok) {
    return { content, structuredContent: payload };
  }
  return { content, structuredContent: payload, isError: true };
}
